import type * as types from "./types";

import * as errors from "./errors";

const getValidatedUrl = (url: string) => {
  try {
    return new URL(url);
  } catch (e) {
    throw new errors.InvalidUrlError(url);
  }
};

function* searchParamsIterator<Q extends types.QueryForm>(
  params: Q,
): IterableIterator<[string, types.InferQueryFormValue<Q>]> {
  if (params instanceof URLSearchParams) {
    yield* params.entries() as IterableIterator<
      [string, types.InferQueryFormValue<Q>]
    >;
  } else if (params instanceof Map) {
    yield* params.entries() as IterableIterator<
      [string, types.InferQueryFormValue<Q>]
    >;
  } else if (Array.isArray(params)) {
    for (const { key, value } of params) {
      yield [key, value];
    }
  } else {
    let k: keyof Q;
    for (k in params) {
      yield [k, params[k] as types.InferQueryFormValue<Q>];
    }
  }
}

const QueryParamInitialStates: {
  [key in types.QueryFormKey]: types.QueryParamFormInitialState<{ as: key }>;
} = {
  "key-values": [],
  map: () => new Map(),
  object: {},
  "search-params": () => new URLSearchParams(),
};

const QueryParamTransformers: {
  [key in types.QueryFormKey]: types.QueryParamTransformer<{ as: key }>;
} = {
  "key-values": (existing, key, value) => [...existing, { key, value }],
  object: (existing, key, value) => ({ ...existing, [key]: value }),
  map: (existing, key, value) => {
    existing.set(key, value);
    return existing;
  },
  "search-params": (existing, key, value) => {
    if (value !== null && value !== undefined) {
      existing.set(key, String(value));
    }
    return existing;
  },
};

/**
 * Transforms the provided query parameters, {@link Q}, from one form to another, defined by the
 * provided {@link QueryParamTransformOptions}, {@link O}.
 *
 * @param {Q} params
 *   The query parameters in their original form that should be transformed to their new form.
 *
 * @param {O} options
 *   Options that dictate the form in which the query parameters {@link Q} should be transformed
 *   and returned.
 *
 * @returns
 *   The transformed query parameters.
 */
export const transformQueryParams = <
  Q extends types.QueryForm<types.QueryFormKey, V>,
  O extends types.QueryParamTransformOptions,
  V extends types.QueryParamValue,
>(
  params: Q,
  options?: O,
): types.QueryForm<
  types.QueryParamTransformKey<O>,
  types.InferQueryFormValue<Q>
> => {
  const transformer = QueryParamTransformers[
    options?.as || "search-params"
  ] as types.QueryParamTransformer<O>;
  const initial = QueryParamInitialStates[options?.as || "search-params"];
  return [...searchParamsIterator(params)].reduce(
    (prev: types.QueryForm<types.QueryParamTransformKey<O>>, [k, v]) =>
      transformer(prev, k, v),
    (typeof initial === "function" ? initial() : initial) as types.QueryForm<
      types.QueryParamTransformKey<O>
    >,
  );
};

type GetQueryParamOptions = types.QueryParamTransformOptions & {
  readonly enforceValidUrl?: boolean;
};

/**
 * Parses the query parameters from the provided URL, {@link string}, and returns the query
 * parameters as an instance of URLSearchParamsx.
 *
 * @param {string} url The URL that the query parameters should be parsed from.
 *
 * @param {GetQueryParamOptions} options
 *   Options to parse the URL.  Currently, this includes the following options:
 *
 *   1. 'enforceValidUrl'
 *       If explicitly provided as false, the query string will not enforce that the provided url
 *       is a valid URL, but will attempt to parse the query parameters based on string logic.  If
 *       provided as true, or not provided at all, an error will be thrown if the value is not a
 *       valid URL.
 *
 * @returns {URLSearchParams}
 * 	 The {@link URLSearchParams} instance that contains the query parameter key
 *   value pairs from the parsed URL.
 */
export const getQueryParams = <O extends GetQueryParamOptions>(
  url: string,
  options?: O,
): types.QueryForm<types.QueryParamTransformKey<O>, string> => {
  let queryString: string;

  if (options?.enforceValidUrl === false) {
    const parts = url.split("?");
    if (parts.length > 2) {
      throw new errors.InvalidUrlError(url);
    } else if (parts.length === 1) {
      queryString = "";
    } else {
      queryString = parts[1];
    }
  } else {
    queryString = getValidatedUrl(url).search;
  }
  if (queryString.length !== 0) {
    return transformQueryParams(new URLSearchParams(queryString), options);
  }
  return transformQueryParams(new URLSearchParams(), options);
};

/**
 * Encodes all provided sets of query parameters after merging each set of query parameters with
 * the next, returning the overall {@link URLSearchParams} instance.
 *
 * Each set of query parameters, {@link QueryForm}, can be provided as either:
 *
 * 1. A {@link URLSearchParams} instance.
 * 2. An object-type, {@link RawQuery}, containing the key-value pairs representing the query
 *    parameters.
 * 3. A map-type, {@link Map<string, QueryParamValue>}, containing the key-value pairs representing
 *    the query parameters.
 * 4. An array of key-value pair objects, {@link RawQueryKV}, representing the query parameters.
 *
 * @example
 * // Returns "fruit=apple&color=orange&page=1&pageSize=10"
 * encodeQueryParams(
 *   { fruit: "apple", color: "red" },
 *   { page: 1, pageSize: 10, color: "orange" }
 * ).toString()
 *
 * @param {P[]} params
 *   The sets of query parameters that should be merged together and encoded in the resulting
 *   {@link URLSearchParams} object.  Query parameters that exist in latter sets, {@link P}, will
 *   overwrite those provided in earlier sets, {@link P}.  A query parameter that has a value of
 *   {@link null} or "" will remove that query parameter from the set if defined earlier.
 *
 * @returns {URLSearchParams}
 * 	 The {@link URLSearchParams} instance representing the cumulatively encoded query parameters in
 *   each set.
 */
export function mergeQueryParams<P extends types.QueryForm>(
  ...params: P[]
): URLSearchParams {
  const newSearchParams = new URLSearchParams();

  for (const paramSet of params) {
    const iterator = searchParamsIterator(paramSet);
    for (const [k, v] of iterator) {
      const existing = newSearchParams.get(k);
      if (v === null && existing !== null) {
        newSearchParams.delete(k);
      } else if (v !== undefined && v !== null) {
        newSearchParams.set(k, typeof v === "string" ? v : v.toString());
      }
    }
  }
  return newSearchParams;
}

type AddQueryParamsToUrlOptions = GetQueryParamOptions & {
  /**
   * Whether or not the provided set of query parameters should be used in place of any existing
   * parameters on the URL, or the provided set of query parameters should be used in addition to
   * any existing parameters on the URL.
   */
  readonly overwrite?: boolean;
};

/**
 * Adds the provided query parameters to the provided URL, {@link string}, either overwriting the
 * existing parameters entirely or merging with the existing parameters on the provided URL.
 *
 * The query parameters to add to the URL can be provided in a number of ways.
 * @see encodeQueryParams
 *
 * @param {string} url The URL for which the query parameters should be added.
 *
 * @param {QueryForm} params The query parameters to add to the URL.
 *
 * @param {AddQueryParamsToUrlOptions} options
 *   Options indicating the behavior of the method, namely whether or not the query parameters
 *   should be used in place of existing query parameters or merged with existing query parameters
 *   on the URL.
 *   @see AddQueryParamsToUrlOptions
 *   @see GetQueryParamOptions
 *
 * @returns {string} The URL with query parameters added.
 */
export function addQueryParamsToUrl(
  url: string,
  params: types.QueryForm,
  options?: AddQueryParamsToUrlOptions,
): string {
  const overwrite =
    options?.overwrite === undefined ? false : options.overwrite;
  let encoded: URLSearchParams;
  if (overwrite) {
    /* Since the URL will not be validated based on the `getQueryParams` method, we have to manually
       validate it here. */
    url =
      options?.enforceValidUrl === false
        ? url
        : getValidatedUrl(url).toString();
    encoded = mergeQueryParams(params);
  } else {
    encoded = mergeQueryParams(getQueryParams(url, options), params);
  }
  if (encoded.toString() !== "") {
    return url.split("?")[0] + "?" + encoded.toString();
  }
  return url;
}
